import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton
} from '@mui/material';
import {
  Business,
  AccessTime,
  ArrowForward,
  Description,
  LocationOn
} from '@mui/icons-material';
import api from '../services/api';
import { brand } from '../theme/colors';

const MerchantQuoteRequestsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [quoteRequests, setQuoteRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all'); // 'all', 'pending', 'quotes_received'

  useEffect(() => {
    loadQuoteRequests();
  }, [filter]);

  const loadQuoteRequests = async () => {
    try {
      setLoading(true);
      setError('');

      const params = {};
      if (filter !== 'all') {
        params.status = filter;
      }

      const response = await api.get('/merchant-quotes/merchant/requests', { params });

      if (response.data.success) {
        setQuoteRequests(response.data.data || []);
      } else {
        setError(response.data.error || 'Failed to load quote requests');
      }
    } catch (err) {
      console.error('Error loading quote requests:', err);
      console.error('Error response:', err.response?.data);
      setError(err.response?.data?.error || err.message || 'Failed to load quote requests');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'sent': return 'info';
      case 'quotes_received': return 'success';
      case 'expired': return 'error';
      default: return 'default';
    }
  };

  const formatDeadline = (deadline) => {
    if (!deadline) return 'N/A';
    const date = new Date(deadline);
    const now = new Date();
    const diff = date - now;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days < 0) return 'Expired';
    if (days === 0) return 'Due today';
    if (days === 1) return 'Due tomorrow';
    return `${days} days remaining`;
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Quote Requests
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant={filter === 'all' ? 'contained' : 'outlined'}
            onClick={() => setFilter('all')}
            size="small"
          >
            All
          </Button>
          <Button
            variant={filter === 'sent' ? 'contained' : 'outlined'}
            onClick={() => setFilter('sent')}
            size="small"
          >
            Pending
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {quoteRequests.length === 0 ? (
        <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No quote requests found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            You'll see quote requests from clients here when they request quotes for materials.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {quoteRequests.map((request) => (
            <Grid item xs={12} key={request.id}>
              <Card
                sx={{
                  cursor: 'pointer',
                  '&:hover': { boxShadow: 4 },
                  transition: 'box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
                onClick={() => navigate(`/merchant/quote-request/${request.id}`)}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        {request.project_title || 'Project'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Client: {request.client_name || 'N/A'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Chip
                        label={request.status}
                        color={getStatusColor(request.status)}
                        size="small"
                      />
                      <IconButton size="small">
                        <ArrowForward />
                      </IconButton>
                    </Box>
                  </Box>

                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <LocationOn sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2">
                          {request.delivery_address?.city || 'N/A'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AccessTime sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2">
                          Deadline: {formatDeadline(request.quote_deadline)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">
                        Request Type: {request.request_type === 'boq_upload' ? 'BOQ Upload' : 'Material Form'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Categories: {request.material_categories?.join(', ') || 'N/A'}
                      </Typography>
                    </Grid>
                  </Grid>

                  {request.boq_file_url && (
                    <Button
                      size="small"
                      startIcon={<Description />}
                      variant="outlined"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(request.boq_file_url, '_blank');
                      }}
                      sx={{ mr: 1 }}
                    >
                      View BOQ
                    </Button>
                  )}

                  <Button
                    size="small"
                    variant="contained"
                    endIcon={<ArrowForward />}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/merchant/quote-request/${request.id}`);
                    }}
                    sx={{
                      background: brand.gradient,
                      '&:hover': {
                        background: brand.gradientHover
                      }
                    }}
                  >
                    Prepare Quote
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};

export default MerchantQuoteRequestsPage;

